import {tx, ptx, usePageText} from '../../lib/page-text';
import { useEffect, useRef, useState, type RefObject } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api-client";
import { useAuth } from "../../lib/auth-store";
import {
  captionsVtt,
  captionMarkup,
  vttTime,
  type CaptionCue,
  type CaptionTrack,
  type ReplayCaptionData,
} from "../../../../api/src/live-program/caption-format";
const failure = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ??
  (e as Error).message ??
  "Could not update captions. Try again.";
const hostPath = (streamId: string, recordingId: string) =>
  `/me/streams/${streamId}/program/replays/${recordingId}/captions`;
function download(track: CaptionTrack, format: "vtt" | "txt") {
  const content =
    format === "vtt"
      ? captionsVtt(track.cues)
      : track.cues.map((c) => `${vttTime(c.start)}  ${c.text}`).join("\n\n");
  const url = URL.createObjectURL(
    new Blob([content], {
      type:
        format === "vtt"
          ? "text/vtt;charset=utf-8"
          : "text/plain;charset=utf-8",
    }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `captions-${track.language}.${format}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function ReplayCaptionEditor({
  streamId,
  recordingId,
}: {
  streamId: string;
  recordingId: string;
}) {
  usePageText();
  const canEdit = useAuth((s) =>
    ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
  );
  const [open, setOpen] = useState(false),
    [language, setLanguage] = useState("en"),
    [label, setLabel] = useState("English"),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [drag, setDrag] = useState(false),
    [shift, setShift] = useState("0"),
    [search, setSearch] = useState(""),
    [limit, setLimit] = useState(50);
  const [editing, setEditing] = useState<
    (CaptionCue & { index: number }) | null
  >(null);
  const file = useRef<HTMLInputElement>(null),
    qc = useQueryClient(),
    prefix = hostPath(streamId, recordingId),
    key = ["replay-captions", streamId, recordingId];
  const q = useQuery({
    queryKey: key,
    enabled: open,
    queryFn: async () => (await api.get<ReplayCaptionData>(prefix)).data,
    refetchOnWindowFocus: false,
  });
  const track = q.data?.tracks.find(
    (t) => t.language === language.toLowerCase(),
  );
  async function update(action: string, extra: Record<string, unknown> = {}) {
    if (!q.data) return;
    if (
      action === "remove" &&
      !confirm("Remove this caption language from the replay?")
    )
      return;
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.patch<ReplayCaptionData>(
        prefix + "/" + language,
        { version: q.data.version, action, ...extra },
      );
      qc.setQueryData(key, data);
      setEditing(null);
      setNotice(
        action === "enable"
          ? "Captions enabled for this replay’s audience."
          : action === "remove"
            ? "Caption language removed."
            : action === "disable"
              ? "Captions hidden from the audience."
              : "Saved as draft. Enable captions when ready.",
      );
    } catch (e) {
      setNotice(failure(e));
    } finally {
      setBusy(false);
    }
  }
  async function upload(selected?: File) {
    if (!selected || !q.data || !canEdit) return;
    if (!/\.(vtt|srt)$/i.test(selected.name) || selected.size > 256 * 1024) {
      setNotice("Choose an SRT or VTT file smaller than 256 KB.");
      return;
    }
    if (
      track &&
      !confirm(
        "Replace this language with the uploaded file? It will return to draft.",
      )
    )
      return;
    setBusy(true);
    setNotice("");
    try {
      const form = new FormData();
      form.append("file", selected);
      form.append("version", String(q.data.version));
      form.append("language", language);
      form.append("label", label);
      const { data } = await api.post<ReplayCaptionData>(prefix, form);
      qc.setQueryData(key, data);
      setLanguage(language.toLowerCase());
      setEditing(null);
      setNotice(
        "Uploaded as draft. Review the text and timing, then enable captions.",
      );
    } catch (e) {
      setNotice(failure(e));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  }
  const rows =
    track?.cues
      .map((cue, index) => ({ cue, index }))
      .filter(({ cue }) =>
        cue.text.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
      ) ?? [];
  return (
    <details
      className="replay-caption-editor"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>{tx("Captions & transcript")}</summary>
      {open && (
        <>
          <p className="muted">{tx("Add up to eight languages from SRT or VTT files. Uploads and corrections stay private until enabled. These captions follow the replay’s audience access.")}</p>
          {q.isPending && <p role="status">{tx("Loading captions…")}</p>}
          {q.isError && (
            <p role="alert">{ptx("Could not load captions.{v0}{v1}", {v0: (" "), v1: (<button
                className="btn outline sm"
                onClick={() => void q.refetch()}
              >{tx("Retry captions")}</button>)})}</p>
          )}
          {q.data && (
            <>
              <div
                className="caption-languages"
                role="group"
                aria-label={tx("Caption languages")}
              >
                {q.data.tracks.map((t) => (
                  <button
                    type="button"
                    className="btn outline sm"
                    key={t.language}
                    aria-pressed={t.language === language}
                    onClick={() => {
                      setLanguage(t.language);
                      setLabel(t.label);
                      setEditing(null);
                      setSearch("");
                      setLimit(50);
                    }}
                  >
                    {t.label} · {t.enabled ? tx("Enabled") : tx("Draft")}
                  </button>
                ))}
              </div>
              {canEdit && (
                <fieldset disabled={busy} className="caption-fields">
                  <div className="program-row">
                    <label>{ptx("Language code{v0}", {v0: (<input
                        className="input"
                        value={language}
                        maxLength={35}
                        placeholder={tx("en, ar, zh-Hans")}
                        onChange={(e) => {
                          setLanguage(e.target.value);
                          setEditing(null);
                        }}
                      />)})}</label>
                    <label>{ptx("Language label{v0}", {v0: (<input
                        className="input"
                        value={label}
                        maxLength={60}
                        onChange={(e) => setLabel(e.target.value)}
                      />)})}</label>
                  </div>
                  <input
                    ref={file}
                    hidden
                    type="file"
                    accept=".vtt,.srt,text/vtt,application/x-subrip"
                    aria-label={tx("Caption file")}
                    onChange={(e) => void upload(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className={`caption-drop ${drag ? "dragging" : ""}`}
                    disabled={!language.trim() || !label.trim()}
                    onClick={() => file.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDrag(true);
                    }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDrag(false);
                      if (!busy) void upload(e.dataTransfer.files?.[0]);
                    }}
                  >
                    <strong>
                      {busy
                        ? tx("Saving captions…")
                        : tx("Drop an SRT or VTT file here")}
                    </strong>
                    <span>{tx("or click to browse · UTF-8 · up to 256 KB / 2,000 lines")}</span>
                  </button>
                  {track && (
                    <>
                      <div className="program-row">
                        <button
                          className="btn outline sm"
                          onClick={() =>
                            void update(track.enabled ? "disable" : "enable")
                          }
                        >
                          {track.enabled
                            ? tx("Disable captions")
                            : tx("Enable captions")}
                        </button>
                        <button
                          className="btn ghost sm"
                          onClick={() => void update("remove")}
                        >{tx("Remove language")}</button>
                      </div>
                      <div className="program-row">
                        <label>{ptx("Shift all captions (seconds){v0}", {v0: (<input
                            className="input"
                            type="number"
                            min={-3600}
                            max={3600}
                            step={0.1}
                            value={shift}
                            onChange={(e) => setShift(e.target.value)}
                          />)})}</label>
                        <button
                          className="btn outline sm"
                          disabled={
                            !shift ||
                            !Number.isFinite(Number(shift)) ||
                            Number(shift) === 0
                          }
                          onClick={() =>
                            void update("shift", { seconds: Number(shift) })
                          }
                        >{tx("Apply timing shift")}</button>
                      </div>
                    </>
                  )}
                </fieldset>
              )}
              {track && (
                <>
                  <div className="program-row">
                    <p>{ptx("{v0} captions ·{v1}{v2}", {v0: (track.cues.length), v1: (" "), v2: (track.enabled ? tx("Enabled") : tx("Draft"))})}</p>
                    <button
                      className="btn ghost sm"
                      onClick={() => download(track, "vtt")}
                    >{tx("Download VTT")}</button>
                  </div>
                  <label>{ptx("Find caption{v0}", {v0: (<input
                      className="input"
                      type="search"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setLimit(50);
                      }}
                    />)})}</label>
                  <div className="caption-editor-list">
                    {rows.slice(0, limit).map(({ cue, index }) => (
                      <div className="caption-editor-row" key={index}>
                        <span className="muted">
                          {vttTime(cue.start)} → {vttTime(cue.end)}
                        </span>
                        <p dir="auto">{cue.text}</p>
                        {canEdit && (
                          <button
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => setEditing({ ...cue, index })}
                          >{ptx("Edit caption {v0}", {v0: (index + 1)})}</button>
                        )}
                      </div>
                    ))}
                    {!rows.length && <p>{tx("No matching captions.")}</p>}
                    {rows.length > limit && (
                      <button
                        className="btn outline sm"
                        onClick={() => setLimit(limit + 50)}
                      >{tx("Show more captions")}</button>
                    )}
                  </div>
                  {editing && (
                    <fieldset className="caption-fields" disabled={busy}>
                      <legend>{ptx("Edit caption {v0}", {v0: (editing.index + 1)})}</legend>
                      <div className="program-row">
                        <label>{ptx("Start (seconds){v0}", {v0: (<input
                            className="input"
                            type="number"
                            step={0.001}
                            min={0}
                            value={editing.start}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                start: Number(e.target.value),
                              })
                            }
                          />)})}</label>
                        <label>{ptx("End (seconds){v0}", {v0: (<input
                            className="input"
                            type="number"
                            step={0.001}
                            min={0}
                            value={editing.end}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                end: Number(e.target.value),
                              })
                            }
                          />)})}</label>
                      </div>
                      <label>{ptx("Caption text{v0}", {v0: (<textarea
                          className="input"
                          dir="auto"
                          rows={3}
                          maxLength={2000}
                          value={editing.text}
                          onChange={(e) =>
                            setEditing({ ...editing, text: e.target.value })
                          }
                        />)})}</label>
                      <div className="program-row">
                        <button
                          className="btn"
                          onClick={() => void update("edit", { cue: editing })}
                        >{tx("Save caption")}</button>
                        <button
                          className="btn ghost"
                          onClick={() => setEditing(null)}
                        >{tx("Cancel edit")}</button>
                      </div>
                    </fieldset>
                  )}
                </>
              )}
            </>
          )}
          {notice && <p role="status">{notice}</p>}
          <button
            className="btn ghost sm"
            disabled={busy}
            onClick={() => {
              setEditing(null);
              setNotice("");
              void q.refetch();
            }}
          >{tx("Reload captions")}</button>
        </>
      )}
    </details>
  );
}
export function ReplayCaptions({
  streamId,
  recordingId,
  publicReplay,
  videoRef,
  time,
  onSeek,
}: {
  streamId?: string;
  recordingId: string;
  publicReplay?: { key: string; token?: string };
  videoRef: RefObject<HTMLVideoElement | null>;
  time: number;
  onSeek: (time: number) => void;
}) {
  usePageText();
  const [language, setLanguage] = useState(""),
    [search, setSearch] = useState(""),
    [limit, setLimit] = useState(50);
  const native = useRef<TextTrack | null>(null);
  const q = useQuery({
    queryKey: [
      "replay-captions",
      streamId,
      recordingId,
      publicReplay?.key,
      publicReplay?.token,
    ],
    enabled: !!(streamId || publicReplay?.key),
    queryFn: async () =>
      (
        await api.get<ReplayCaptionData>(
          publicReplay
            ? `/streams/${encodeURIComponent(publicReplay.key)}/program/replays/${recordingId}/captions`
            : hostPath(streamId!, recordingId),
          { params: { t: publicReplay?.token } },
        )
      ).data,
    refetchInterval: 15000,
    retry: false,
  });
  const track = !q.isError
    ? q.data?.tracks.find((t) => t.language === language)
    : undefined;
  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof VTTCue === "undefined" || (!track && !native.current))
      return;
    const textTrack =
      native.current ?? video.addTextTrack("captions", "Replay captions");
    native.current = textTrack;
    // Disabled TextTrack.cues is null in browsers; expose it before removing old cues.
    textTrack.mode = "hidden";
    for (const cue of Array.from(textTrack.cues ?? []))
      textTrack.removeCue(cue);
    for (const c of track?.cues ?? [])
      textTrack.addCue(new VTTCue(c.start, c.end, captionMarkup(c.text)));
    const mode = () => {
      if (!track) {
        textTrack.mode = "disabled";
        return;
      }
      textTrack.mode =
        track &&
        (document.fullscreenElement === video ||
          !!(
            video as HTMLVideoElement & { webkitDisplayingFullscreen?: boolean }
          ).webkitDisplayingFullscreen)
          ? "showing"
          : "hidden";
    };
    mode();
    const nativeEnter = () => {
      if (track) textTrack.mode = "showing";
    };
    const nativeExit = () => {
      textTrack.mode = track ? "hidden" : "disabled";
    };
    document.addEventListener("fullscreenchange", mode);
    video.addEventListener("webkitbeginfullscreen", nativeEnter);
    video.addEventListener("webkitendfullscreen", nativeExit);
    return () => {
      document.removeEventListener("fullscreenchange", mode);
      video.removeEventListener("webkitbeginfullscreen", nativeEnter);
      video.removeEventListener("webkitendfullscreen", nativeExit);
      textTrack.mode = "hidden";
      for (const cue of Array.from(textTrack.cues ?? []))
        textTrack.removeCue(cue);
      textTrack.mode = "disabled";
    };
  }, [track, videoRef]);
  if (q.isError)
    return (
      <p className="muted">{ptx("Captions could not refresh.{v0}{v1}", {v0: (" "), v1: (<button className="btn ghost sm" onClick={() => void q.refetch()}>{tx("Retry captions")}</button>)})}</p>
    );
  if (!q.data?.tracks.length) return null;
  const active =
    track?.cues.filter((c) => c.start <= time && c.end > time) ?? [];
  const rows =
    track?.cues.filter((c) =>
      c.text.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
    ) ?? [];
  return (
    <section className="replay-captions" aria-label={tx("Replay captions")}>
      <div className="program-row">
        <label>{ptx("Caption language{v0}", {v0: (<select
            className="input"
            value={track ? language : ""}
            onChange={(e) => {
              setLanguage(e.target.value);
              setSearch("");
              setLimit(50);
            }}
          >
            <option value="">{tx("Off")}</option>
            {q.data.tracks.map((t) => (
              <option key={t.language} value={t.language}>
                {t.label}
                {!t.enabled && !publicReplay ? tx(" (draft)") : ""}
              </option>
            ))}
          </select>)})}</label>
        {track && (
          <button
            className="btn ghost sm"
            onClick={() => download(track, "vtt")}
          >{tx("Download captions")}</button>
        )}
      </div>
      {track && (
        <>
          <div
            className="replay-caption-band"
            aria-label={tx("Current caption")}
            dir="auto"
          >
            {active.length ? (
              active.map((c, i) => <span key={i}>{c.text}</span>)
            ) : (
              <span className="muted">{tx("Captions appear here during speech.")}</span>
            )}
          </div>
          <details className="replay-transcript">
            <summary>{tx("Search transcript")}</summary>
            <label>{ptx("Search transcript{v0}", {v0: (<input
                className="input"
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setLimit(50);
                }}
              />)})}</label>
            <p className="muted">{tx("Select a line to jump to that moment.")}</p>
            <div className="replay-transcript-lines">
              {rows.slice(0, limit).map((c, i) => (
                <button
                  key={i}
                  className={`caption-seek ${c.start <= time && c.end > time ? "active" : ""}`}
                  onClick={() => onSeek(c.start)}
                >
                  <time dir="ltr">{vttTime(c.start)}</time>
                  <span dir="auto">{c.text}</span>
                </button>
              ))}
              {!rows.length && <p>{tx("No matching captions.")}</p>}
              {rows.length > limit && (
                <button
                  className="btn outline sm"
                  onClick={() => setLimit(limit + 50)}
                >{tx("Show more lines")}</button>
              )}
            </div>
            <button
              className="btn ghost sm"
              onClick={() => download(track, "txt")}
            >{tx("Download transcript")}</button>
          </details>
        </>
      )}
    </section>
  );
}
